<template>
  <div class="admin-tokens-container">
    <Loading :show="loading" />

    <div :class="{ disabled: loading }">
      <h1>Endpoint Tokens</h1>
      <p class="description-text">
        Administrera tokens för övervakning, hälsokontroller och API-rapporter. Det kan maximalt finnas 3 aktiva tokens samtidigt.
      </p>
      <hr />

      <!-- Create Token Form -->
      <div class="add-token-section">
        <h3>Skapa ny token</h3>
        <form @submit.prevent="createToken" class="add-token-form">
          <div class="form-group">
            <label for="token-name">Namn*</label>
            <input
              type="text"
              id="token-name"
              v-model="tokenName"
              placeholder="t.ex. Övervakningstjänst Prod"
              required
            />
          </div>
          <button type="submit" class="button" :disabled="activeCount >= 3">
            Skapa token {{ activeCount >= 3 ? '(Max 3 aktiva)' : '' }}
          </button>
        </form>
      </div>

      <!-- Created Token Display Modal / Alert -->
      <div v-if="createdTokenPlain" class="created-token-modal">
        <div class="modal-content">
          <p>
            Denna token visas <strong>endast denna gång</strong>. När du stänger rutan är den krypterad i databasen och kan inte visas igen.
          </p>
          <div class="token-value-box">
            <code>{{ createdTokenPlain }}</code>
            <button @click="copyToken" class="button">
              {{ copied ? 'Kopierad!' : 'Kopiera' }}
            </button>
          </div>
          <button @click="createdTokenPlain = ''" class="button">Jag har sparat tokenen</button>
        </div>
      </div>

      <hr />

      <!-- List of Tokens -->
      <div class="tokens-list-section">
        <h3>Befintliga tokens ({{ tokens.length }})</h3>
        <table class="tokens-table">
          <thead>
            <tr>
              <th>Namn</th>
              <th>Token</th>
              <th>Status</th>
              <th>Anrop</th>
              <th>Giltig till</th>
              <th>Loggade IP-adresser</th>
              <th>Skapad</th>
              <th>Åtgärd</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="token in tokens" :key="token.id">
              <td class="name-cell"><strong>{{ token.name }}</strong></td>
              <td><code class="masked-token-code">{{ token.masked_token || '-' }}</code></td>
              <td>
                <span v-if="token.currently_valid" class="status-badge active">Aktiv</span>
                <span v-else-if="token.is_expired" class="status-badge expired">Utgången</span>
                <span v-else class="status-badge inactive">Inaktiverad</span>
              </td>
              <td><strong>{{ token.request_count || 0 }}</strong> st</td>
              <td>{{ formatDate(token.expiry_date) }}</td>
              <td class="ips-cell">
                <div v-if="token.accessed_ips && token.accessed_ips.length > 0">
                  <span v-for="ip in token.accessed_ips" :key="ip" class="ip-tag">{{ ip }}</span>
                </div>
                <span v-else class="text-muted"></span>
              </td>
              <td>{{ formatDate(token.created_at) }}</td>
              <td>
                <button
                  v-if="token.is_active"
                  @click="deactivateToken(token)"
                  class="btn btn-delete"
                >
                  Inaktivera
                </button>
                <span v-else class="text-muted">-</span>
              </td>
            </tr>
            <tr v-if="tokens.length === 0">
              <td colspan="8" class="empty-cell">Inga tokens hittades.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/components/Loading';
import API from '@/classes/API';
import moment from 'moment';

export default {
  components: {
    Loading,
  },
  data() {
    return {
      loading: false,
      tokens: [],
      tokenName: '',
      createdTokenPlain: '',
      copied: false,
    };
  },
  computed: {
    activeCount() {
      return this.tokens.filter(t => t.currently_valid).length;
    },
  },
  mounted() {
    this.fetchTokens();
  },
  methods: {
    async fetchTokens() {
      this.loading = true;
      try {
        const response = await API.get('/admin/tokens');
        if (response && response.success) {
          this.tokens = response.tokens || [];
        }
      } catch (error) {
        this.$toast.error('Kunde inte hämta tokens.', 'Fel', { position: 'topRight' });
      } finally {
        this.loading = false;
      }
    },
    async createToken() {
      if (!this.tokenName) {
        return;
      }

      this.loading = true;
      try {
        const response = await API.post('/admin/tokens', {
          name: this.tokenName,
        });

        if (response && response.success) {
          this.$toast.success('Token har skapats.', 'Framgång', { position: 'topRight' });
          this.createdTokenPlain = response.plain_token;
          this.tokenName = '';
          await this.fetchTokens();
        } else {
          this.$toast.error((response && response.errorMessage) || 'Det gick inte att skapa token.', 'Fel', { position: 'topRight' });
        }
      } catch (error) {
        const errMsg = error.response && error.response.data && error.response.data.errorMessage
          ? error.response.data.errorMessage
          : 'Kunde inte skapa token.';
        this.$toast.error(errMsg, 'Fel', { position: 'topRight' });
      } finally {
        this.loading = false;
      }
    },
    async deactivateToken(token) {
      if (!confirm(`Är du säker på att du vill inaktivera token '${token.name}'? Detta kan inte ångras.`)) {
        return;
      }

      this.loading = true;
      try {
        const response = await API.post(`/admin/tokens/${token.id}/deactivate`);
        if (response && response.success) {
          this.$toast.success('Token har inaktiverats.', 'Uppdaterat', { position: 'topRight' });
          await this.fetchTokens();
        } else {
          this.$toast.error((response && response.errorMessage) || 'Kunde inte inaktivera token.', 'Fel', { position: 'topRight' });
        }
      } catch (error) {
        this.$toast.error('Kunde inte inaktivera token.', 'Fel', { position: 'topRight' });
      } finally {
        this.loading = false;
      }
    },
    copyToken() {
      if (!this.createdTokenPlain) return;
      navigator.clipboard.writeText(this.createdTokenPlain).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return moment(dateStr).format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>

<style scoped lang="stylus">
@import '~@/global.styl'

.admin-tokens-container
  padding 20px
  max-width 1100px
  margin 0 auto

.description-text
  color #666
  font-size 14px
  margin-bottom 20px

.add-token-section
  background #f9f9f9
  padding 20px
  border-radius 6px
  margin-bottom 20px

.add-token-form
  display flex
  gap 15px
  align-items flex-end

  .form-group
    flex 1
    display flex
    flex-direction column
    label
      font-size 13px
      font-weight 600
      margin-bottom 5px
    input
      padding 10px
      border 1px solid #ccc
      border-radius 4px
      font-size 14px

.btn
  padding 10px 18px
  border-radius 4px
  border none
  cursor pointer
  font-weight 600
  font-size 14px
  &.btn-add
    background #2e7d32
    color #fff
    &:hover
      background #1b5e20
    &:disabled
      background #ccc
      cursor not-allowed
  &.btn-delete
    background #d32f2f
    color #fff
    font-size 12px
    padding 6px 12px
    &:hover
      background #b71c1c
  &.btn-copy
    background #1976d2
    color #fff
    margin-left 10px
  &.btn-close
    background #333
    color #fff
    margin-top 15px

.created-token-modal
  background #e8f5e9
  border 2px solid #2e7d32
  padding 20px
  border-radius 8px
  margin 20px 0

.token-value-box
  display flex
  align-items center
  background #fff
  padding 10px 15px
  border-radius 4px
  border 1px solid #a5d6a7
  margin-top 10px
  code
    font-family monospace
    font-size 16px
    font-weight bold
    color #1b5e20
    word-break break-all

.tokens-table
  width 100%
  border-collapse collapse
  margin-top 15px

  th, td
    padding 12px
    text-align left
    border-bottom 1px solid #eee

  th
    background #f5f5f5
    font-weight 600

.masked-token-code
  background #f0f0f0
  padding 3px 6px
  border-radius 4px
  font-family monospace
  font-size 13px
  color #333

.status-badge
  display inline-block
  padding 4px 8px
  border-radius 12px
  font-size 12px
  font-weight bold
  &.active
    background #e8f5e9
    color #2e7d32
  &.expired
    background #fff3e0
    color #e65100
  &.inactive
    background #ffebee
    color #c62828

.ip-tag
  display inline-block
  background #e3f2fd
  color #1565c0
  padding 2px 6px
  border-radius 4px
  font-size 11px
  font-family monospace
  margin-right 4px
  margin-bottom 4px

.text-muted
  color #999
  font-size 13px

.empty-cell
  text-align center
  color #888
  padding 20px
</style>



// WEBPACK FOOTER //
// Tokens.vue?69c36f37